import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  BankTransactionStatusFilter,
  PaymentProcessorKind,
  PreflightCopy,
  stateFilerIdLabel,
} from '@signalvote/shared';
import type {
  OnboardingSubmitRequest,
  ProcessorRateOverride,
} from '@signalvote/shared';
import {
  useOnboardingCatalog,
  useOnboardingStatus,
  useSubmitOnboarding,
  useConfirmDiscovery,
  useRemoveOnboardingBank,
  useRemoveOnboardingProcessor,
} from '@/hooks/queries/useFinanceOnboarding';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { cn } from '@/lib/utils';
import { DiscoveryPrompt } from './DiscoveryPrompt';
import { ANEDOT_GROSS_DEPOSITS_INSTRUCTION } from '@/components/finance/detector/AnedotGrossDepositsBanner';
import { CANONICAL_PROCESSOR_RATES } from './canonicalProcessorRates';
import { isProcessorKindVisibleForSubsidiary } from '@/lib/finance/processorSubsidiaryVisibility';
import { LinkBankFlow, BankConnectionsCard } from '@/components/finance/bank-link';
import { readySettings } from '@/lib/financeSettingsRead';

// Temporarily hidden: the manual "Banks you use" checkbox list only pre-creates
// named bank accounts and links them to a parser that already works globally —
// it does NOT gate file detection or auto-pick accounts (that's done by header
// fingerprinting + the inline account picker at import time), so its "get matched
// to the right account" promise overstates what it does. Hidden pending a
// decision to remove it or reword it. Flip to true to restore the section.
const SHOW_MANUAL_BANK_LIST = false;

export function OnboardingPage() {
  const catalogRead = useOnboardingCatalog();
  const statusRead = useOnboardingStatus();
  const catalog = catalogRead.status === 'ready' ? catalogRead.data : undefined;
  const status = statusRead.status === 'ready' ? statusRead.data : undefined;
  const { mutateAsync: submitOnboarding, isPending } = useSubmitOnboarding();
  const { mutateAsync: removeBank } = useRemoveOnboardingBank();
  const { mutateAsync: removeProcessor } = useRemoveOnboardingProcessor();
  // ADR-022 — the filer-ID field is jurisdiction-aware: its label/placeholder
  // come from the shared render-boundary helper keyed on the campaign's
  // rulesetCode (generic "State Filer ID" until the jurisdiction is known).
  const financeSettings = readySettings(useFinanceSettings());
  const filerIdLabel = stateFilerIdLabel(financeSettings?.rulesetCode);
  const navigate = useNavigate();

  const [selectedBankIds, setSelectedBankIds] = useState<Set<string>>(new Set());
  const [selectedKinds, setSelectedKinds] = useState<Set<PaymentProcessorKind>>(new Set());
  const [customBank, setCustomBank] = useState('');
  const [customProcessor, setCustomProcessor] = useState('');
  const [stateFilerId, setStateFilerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Per-kind inline fee errors (blank/out-of-range), keyed by PaymentProcessorKind.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Which banks/processors are ALREADY SAVED on the server. Unchecking one of
  // these removes it (confirm → DELETE); unchecking a freshly-ticked (unsaved)
  // one is purely local. Seeded once from status alongside the editable form.
  const [savedBankProfileIds, setSavedBankProfileIds] = useState<Set<string>>(new Set());
  const [savedKinds, setSavedKinds] = useState<Set<PaymentProcessorKind>>(new Set());
  // Inline removal errors (e.g. the 409 "still has imported data" message),
  // keyed by `bank:<profileId>` / `proc:<kind>`, rendered next to the item.
  const [removalErrors, setRemovalErrors] = useState<Record<string, string>>({});

  // Per-kind fee-entry state. Keyed by PaymentProcessorKind; the form maps to
  // ProcessorRateOverride entries on submit. Strings (not numbers) so the input
  // renders the user's typed value; we parse + validate at submit time. We do
  // NOT default or interpret published rates — an empty string is a
  // required-field error (blocks submit), not a "use canonical default" signal.
  const [feeRateInputs, setFeeRateInputs] = useState<Record<string, string>>({});
  const [flatFeeInputs, setFlatFeeInputs] = useState<Record<string, string>>({});

  // Seed the editable form ONCE from what the treasurer already saved, so the
  // whole config is visible on reload — this is an ongoing settings page, not a
  // one-shot form. One-time seed via a ref: the form is user-owned after this,
  // and re-syncing would clobber in-progress edits (including after a removal
  // refetch). CRITICAL: the seed fires as soon as status FIRST loads, regardless
  // of whether any processor exists — a banks-only campaign must still restore
  // its banks (the previous gate on `processors.length` left it empty on reload).
  // Persistence stores fee rates as fractions (0.036); the input renders
  // percentage (3.6), so convert on the way in.
  const seededFromStatusRef = useRef(false);
  useEffect(() => {
    if (seededFromStatusRef.current) return;
    if (!status) return;
    seededFromStatusRef.current = true;

    // Banks — re-check saved profiles and remember which are saved (so unchecking
    // one triggers a removal rather than a purely-local uncheck).
    const bankIds = new Set<string>(status.bankProfileIds ?? []);
    setSelectedBankIds(bankIds);
    setSavedBankProfileIds(new Set(bankIds));
    // Custom bank free-text — restore the first typed name (single-field UI).
    const firstCustomBank = (status.customBankNames ?? [])[0];
    if (firstCustomBank) setCustomBank(firstCustomBank);

    // State filer id.
    if (status.stateFilerId) setStateFilerId(status.stateFilerId);

    // Processors — selected kinds + saved-rate pre-fill.
    const processors = status.processors ?? [];
    const kinds = new Set<PaymentProcessorKind>();
    const rates: Record<string, string> = {};
    const flats: Record<string, string> = {};
    let otherName = '';
    for (const p of processors) {
      if (p.kind === PaymentProcessorKind.Other) otherName = p.displayName ?? '';
      else kinds.add(p.kind);
      // Fraction → percentage, trailing zeros trimmed (0.036 → "3.6").
      if (p.feeRate > 0) rates[p.kind] = (p.feeRate * 100).toFixed(2).replace(/\.?0+$/, '');
      if (p.flatFeePerTransaction != null) flats[p.kind] = p.flatFeePerTransaction.toFixed(2);
    }
    setSelectedKinds(kinds);
    setSavedKinds(new Set(processors.map((p) => p.kind)));
    setFeeRateInputs(rates);
    setFlatFeeInputs(flats);
    if (otherName) setCustomProcessor(otherName);
  }, [status]);

  // The form seeds from the saved status, so it waits for it: a submit before the status lands
  // would save an empty form over the treasurer's banks and processors.
  const unready = [catalogRead, statusRead].find((r) => r.status === 'failed' || r.status === 'offline');
  if (unready) {
    return (
      <div className="p-6 text-sm text-red-700" role="alert">
        {unready.status === 'offline'
          ? "You're offline, so your banks and processors can't load."
          : "Couldn't load your banks and processors. Refresh to try again."}
      </div>
    );
  }
  if (!catalog || !status) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const toggleBank = (id: string) => {
    // Unchecking a SAVED bank is a removal (confirm → DELETE); everything else is
    // a purely-local toggle. A newly-ticked-then-unticked bank was never saved,
    // so it must NOT hit the server.
    if (selectedBankIds.has(id) && savedBankProfileIds.has(id)) {
      void requestRemoveBank(id);
      return;
    }
    setSelectedBankIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleProcessor = (kind: PaymentProcessorKind) => {
    if (selectedKinds.has(kind) && savedKinds.has(kind)) {
      void requestRemoveProcessor(kind);
      return;
    }
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  // Remove a saved bank. On success, drop it from the local + saved sets and
  // clear any prior inline error; on a 409 conflict, keep it checked and surface
  // the treasurer-readable message inline (the server message is already safe —
  // counts, no engineer enum names). The onboarding-status query is invalidated
  // by the hook's onSettled so counts reconcile.
  const requestRemoveBank = async (id: string) => {
    const confirmed = window.confirm(
      "Remove this bank? Statements and files you upload won't be matched to it anymore. You can add it back anytime.",
    );
    if (!confirmed) return;
    try {
      await removeBank(id);
      setSelectedBankIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setSavedBankProfileIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setRemovalErrors((prev) => {
        const { [`bank:${id}`]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      setRemovalErrors((prev) => ({
        ...prev,
        [`bank:${id}`]: e instanceof Error ? e.message : 'Could not remove this bank.',
      }));
    }
  };

  const requestRemoveProcessor = async (kind: PaymentProcessorKind) => {
    const confirmed = window.confirm(
      "Remove this processor? Files and transactions from it won't be matched to your campaign anymore. You can add it back anytime.",
    );
    if (!confirmed) return;
    try {
      await removeProcessor(kind);
      setSelectedKinds((prev) => {
        const next = new Set(prev);
        next.delete(kind);
        return next;
      });
      setSavedKinds((prev) => {
        const next = new Set(prev);
        next.delete(kind);
        return next;
      });
      setRemovalErrors((prev) => {
        const { [`proc:${kind}`]: _removed, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      setRemovalErrors((prev) => ({
        ...prev,
        [`proc:${kind}`]: e instanceof Error ? e.message : 'Could not remove this processor.',
      }));
    }
  };

  const handleSave = async () => {
    setError(null);
    const payload: OnboardingSubmitRequest = {
      bankProfileIds: Array.from(selectedBankIds),
      processorKinds: Array.from(selectedKinds),
      customBanks: null,
      customProcessorNames: null,
      stateFilerId: null,
      processorRateOverrides: null,
    };
    const trimmedBank = customBank.trim();
    if (trimmedBank) payload.customBanks = [trimmedBank];
    const trimmedProcessor = customProcessor.trim();
    if (trimmedProcessor) payload.customProcessorNames = [trimmedProcessor];
    const trimmedFilerId = stateFilerId.trim();
    if (trimmedFilerId) payload.stateFilerId = trimmedFilerId;

    // Force-entry: for every selected processor EXCEPT ManualP2P (Venmo/Zelle/
    // Cash App — no processor fee by nature), the treasurer must explicitly
    // enter BOTH the fee rate and the per-transaction fee. Product decision: we
    // don't default or interpret published rates, so a blank field is a
    // required-field error that blocks submit — never a fall-back-to-canonical
    // signal. An explicit "0" is a valid entry; only truly-empty is invalid.
    // The server re-validates the same rule (defense in depth).
    const overrides: ProcessorRateOverride[] = [];
    const nextFieldErrors: Record<string, string> = {};
    const kindsNeedingRate: PaymentProcessorKind[] = [
      ...Array.from(selectedKinds),
      ...(trimmedProcessor ? [PaymentProcessorKind.Other] : []),
    ];
    for (const kind of kindsNeedingRate) {
      const isManualP2P = kind === PaymentProcessorKind.ManualP2P;
      const rateStr = feeRateInputs[kind]?.trim() ?? '';
      const flatStr = flatFeeInputs[kind]?.trim() ?? '';

      if (!isManualP2P && (rateStr === '' || flatStr === '')) {
        nextFieldErrors[kind] = PreflightCopy.ProcessorFeesRequired;
        continue;
      }

      let feeRate: number | null = null;
      if (rateStr !== '') {
        const parsed = parseFloat(rateStr);
        if (Number.isNaN(parsed)) {
          nextFieldErrors[kind] = PreflightCopy.ProcessorRateOutOfRangeError;
          continue;
        }
        // UI is percentage (3.94); persistence is fraction (0.0394). Single
        // /100 conversion lives here so the input renders the natural form.
        const decimal = parsed / 100;
        if (decimal < 0 || decimal > 0.10) {
          nextFieldErrors[kind] = PreflightCopy.ProcessorRateOutOfRangeError;
          continue;
        }
        feeRate = decimal;
      }
      let flatFee: number | null = null;
      if (flatStr !== '') {
        const parsed = parseFloat(flatStr);
        if (Number.isNaN(parsed) || parsed < 0 || parsed > 10) {
          nextFieldErrors[kind] = PreflightCopy.ProcessorFlatFeeOutOfRangeError;
          continue;
        }
        flatFee = parsed;
      }
      if (feeRate != null || flatFee != null) {
        overrides.push({ kind, feeRate, flatFeePerTransaction: flatFee });
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      // Prefer the force-entry message when any field is blank; otherwise the
      // specific range error. Both are treasurer-readable.
      const blankMsg = Object.values(nextFieldErrors).find(
        (m) => m === PreflightCopy.ProcessorFeesRequired,
      );
      setError(blankMsg ?? Object.values(nextFieldErrors)[0]);
      return;
    }
    setFieldErrors({});
    if (overrides.length > 0) payload.processorRateOverrides = overrides;

    try {
      await submitOnboarding(payload);
      // USI-005 (ADR-011): post-onboarding lands on the one-phase close hub.
      navigate({ to: '/admin/finance/close' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const handleSkip = () => {
    navigate({ to: '/admin/finance/close' });
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-foreground">Banks &amp; processors</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Tell us where your campaign&apos;s money moves — the banks and payment
          processors you use. This lets us match everything that comes in to the
          right account. You can add or remove any of these here anytime.
        </p>
        {status && (status.bankCount > 0 || status.processorCount > 0) && (
          <p className="mt-2 text-xs text-muted-foreground">
            Currently configured: {status.bankCount} bank
            {status.bankCount === 1 ? '' : 's'}, {status.processorCount} processor
            {status.processorCount === 1 ? '' : 's'}.
          </p>
        )}
      </header>

      {/*
        ADR-070 (#562) — the guided Plaid link flow. Sits at the top of the
        Banks & processors page as the automatic alternative to naming banks by
        hand below. Self-gates on Finance:Manage and renders nothing for anyone
        below that tier.
      */}
      <LinkBankFlow />

      {/*
        ADR-070 (#563) — the connection-health surface. Co-located directly beneath
        the Link-a-bank entry point: lists each linked connection's status and offers
        reconnect (Plaid Link update mode) / disconnect. Self-hides until at least one
        bank is linked and for anyone below Finance:Manage.
      */}
      <BankConnectionsCard />

      {SHOW_MANUAL_BANK_LIST && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Banks you use</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Prefer to upload statements yourself, or don&apos;t see your bank in
              the automatic list above? Check the banks your campaign uses so files
              you upload by hand get matched to the right account.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {catalog.banks.map((b) => (
              <div key={b.id} className="flex flex-col gap-1">
                <label
                  htmlFor={`bank-${b.id}`}
                  className="flex items-center gap-3 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-accent/40"
                >
                  <input
                    id={`bank-${b.id}`}
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedBankIds.has(b.id)}
                    onChange={() => toggleBank(b.id)}
                  />
                  <span className="text-sm">{b.displayName}</span>
                </label>
                {removalErrors[`bank:${b.id}`] && (
                  <p
                    className="text-xs text-red-600 px-1"
                    data-testid={`bank-removal-error-${b.id}`}
                  >
                    {removalErrors[`bank:${b.id}`]}
                  </p>
                )}
              </div>
            ))}
          </div>
          <input
            type="text"
            placeholder="Other bank — name it"
            value={customBank}
            onChange={(e) => setCustomBank(e.target.value)}
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
          />
        </section>
      )}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Payment processors you use
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Check the processors your campaign uses to collect money. For each one,
            enter its fee rate and per-transaction fee — we don&apos;t fill these in
            for you, so check the processor&apos;s dashboard or a recent statement.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {catalog.processors
            .filter((p) => p.kind !== PaymentProcessorKind.Other)
            // Hide the partisan-only platforms in the other party's build (WinRed
            // is GOP-only, ActBlue is Democrat-only).
            .filter((p) => isProcessorKindVisibleForSubsidiary(p.kind))
            .map((p) => (
              <div key={p.kind} className="flex flex-col gap-2">
                <label
                  htmlFor={`proc-${p.kind}`}
                  className={cn(
                    'flex items-start gap-3 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-accent/40',
                  )}
                >
                  <input
                    id={`proc-${p.kind}`}
                    type="checkbox"
                    className="h-4 w-4 mt-0.5"
                    checked={selectedKinds.has(p.kind)}
                    onChange={() => toggleProcessor(p.kind)}
                  />
                  <span className="text-sm">
                    {p.displayName}
                    {p.comingSoon && (
                      <span className="block text-xs text-muted-foreground italic mt-0.5">
                        Support coming soon — files will be saved for when {p.displayName} support ships
                      </span>
                    )}
                  </span>
                </label>
                {removalErrors[`proc:${p.kind}`] && (
                  <p
                    className="text-xs text-red-600 px-1"
                    data-testid={`processor-removal-error-${p.kind}`}
                  >
                    {removalErrors[`proc:${p.kind}`]}
                  </p>
                )}
                {selectedKinds.has(p.kind) && (
                  <ProcessorRateConfirm
                    kind={p.kind}
                    feeRateInput={feeRateInputs[p.kind] ?? ''}
                    flatFeeInput={flatFeeInputs[p.kind] ?? ''}
                    fieldError={fieldErrors[p.kind]}
                    onFeeRateChange={(v) =>
                      setFeeRateInputs((prev) => ({ ...prev, [p.kind]: v }))
                    }
                    onFlatFeeChange={(v) =>
                      setFlatFeeInputs((prev) => ({ ...prev, [p.kind]: v }))
                    }
                  />
                )}
              </div>
            ))}
        </div>
        {/*
          PPR-012: onboarding tooltip variant — non-dismissible, no
          per-campaign localStorage. The inbox variant
          (AnedotGrossDepositsBanner) is dismissible because it nags on
          every Anedot upload; this surface only fires when the treasurer
          is actively ticking the Anedot checkbox so dismissal is implicit
          (untick to dismiss). Distinct data-testid keeps the two surfaces
          independently testable. Copy comes from the shared constant.
        */}
        {selectedKinds.has(PaymentProcessorKind.Anedot) && (
          <div
            data-testid="anedot-gross-deposits-onboarding-hint"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            After signing up: {ANEDOT_GROSS_DEPOSITS_INSTRUCTION}
          </div>
        )}
        <input
          type="text"
          placeholder="Other processor — name it"
          value={customProcessor}
          onChange={(e) => setCustomProcessor(e.target.value)}
          className="w-full rounded-md border border-border px-3 py-2 text-sm"
        />
        {customProcessor.trim() && (
          <ProcessorRateConfirm
            kind={PaymentProcessorKind.Other}
            feeRateInput={feeRateInputs[PaymentProcessorKind.Other] ?? ''}
            flatFeeInput={flatFeeInputs[PaymentProcessorKind.Other] ?? ''}
            fieldError={fieldErrors[PaymentProcessorKind.Other]}
            onFeeRateChange={(v) =>
              setFeeRateInputs((prev) => ({ ...prev, [PaymentProcessorKind.Other]: v }))
            }
            onFlatFeeChange={(v) =>
              setFlatFeeInputs((prev) => ({ ...prev, [PaymentProcessorKind.Other]: v }))
            }
          />
        )}
      </section>

      <section className="space-y-2">
        <label htmlFor="state-filer-id" className="block text-sm font-semibold">
          {filerIdLabel}
          <span className="ml-2 text-xs text-muted-foreground font-normal">
            (optional — can be added later)
          </span>
        </label>
        <input
          id="state-filer-id"
          type="text"
          placeholder={filerIdLabel}
          value={stateFilerId}
          onChange={(e) => setStateFilerId(e.target.value)}
          className="w-full rounded-md border border-border px-3 py-2 text-sm"
        />
      </section>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
        <button
          type="button"
          onClick={handleSkip}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isPending}
          className={cn(
            'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90',
            isPending && 'opacity-60 cursor-not-allowed',
          )}
        >
          {isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      {import.meta.env.DEV && <DiscoveryPromptPreview />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discovery-prompt preview. Wires through the controlled DiscoveryPrompt so
// the manual demo in PPR-002 acceptance criteria is exercisable end-to-end
// before PPR-003 lands. Hidden behind a collapsible section.
// Guarded by import.meta.env.DEV so it doesn't ship to production users.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Per-processor fee-entry panel. Renders below each selected processor checkbox.
// The treasurer must explicitly enter BOTH the fee rate (%) and the
// per-transaction flat fee — we do NOT default or interpret published rates, so
// there is no pre-fill of these fields from the canonical table (persisted saved
// values still seed them via the status effect). The flat-fee field renders
// UNCONDITIONALLY for every processor. ManualP2P is exempt from force-entry and
// shows a "typically no fee" note. The canonical table is used only for the
// non-fee display hints (processor name + dashboard deep-link).
// ---------------------------------------------------------------------------

interface ProcessorRateConfirmProps {
  kind: PaymentProcessorKind;
  feeRateInput: string;
  flatFeeInput: string;
  /** Inline error (blank/out-of-range) surfaced under the fields on failed submit. */
  fieldError?: string;
  onFeeRateChange: (value: string) => void;
  onFlatFeeChange: (value: string) => void;
}

function ProcessorRateConfirm({
  kind,
  feeRateInput,
  flatFeeInput,
  fieldError,
  onFeeRateChange,
  onFlatFeeChange,
}: ProcessorRateConfirmProps) {
  // Canonical table supplies ONLY non-fee display hints (name + dashboard link),
  // never fee values — those are entered by the treasurer, not defaulted.
  const canonical = CANONICAL_PROCESSOR_RATES[kind];
  const isManualP2P = kind === PaymentProcessorKind.ManualP2P;
  const hint = PreflightCopy.ProcessorRateWhereToFindHint
    .replace('{processorName}', canonical.displayName);

  return (
    <div className="ml-7 mb-1 rounded-md border border-border bg-muted/30 px-3 py-2 space-y-2">
      <div className="flex items-center gap-3">
        <label
          htmlFor={`fee-rate-${kind}`}
          className="text-xs font-medium text-foreground min-w-[7rem]"
        >
          {PreflightCopy.ProcessorRateFieldLabel}
        </label>
        <input
          id={`fee-rate-${kind}`}
          type="number"
          step="0.01"
          min="0"
          max="10"
          placeholder="0.00"
          value={feeRateInput}
          onChange={(e) => onFeeRateChange(e.target.value)}
          className="w-24 rounded-md border border-border px-2 py-1 text-sm"
          data-testid={`processor-rate-input-${kind}`}
        />
        <span className="text-xs text-muted-foreground">%</span>
      </div>
      <div className="flex items-center gap-3">
        <label
          htmlFor={`flat-fee-${kind}`}
          className="text-xs font-medium text-foreground min-w-[7rem]"
        >
          {PreflightCopy.ProcessorFlatFeeFieldLabel}
        </label>
        <span className="text-xs text-muted-foreground">$</span>
        <input
          id={`flat-fee-${kind}`}
          type="number"
          step="0.01"
          min="0"
          max="10"
          placeholder="0.00"
          value={flatFeeInput}
          onChange={(e) => onFlatFeeChange(e.target.value)}
          className="w-24 rounded-md border border-border px-2 py-1 text-sm"
          data-testid={`processor-flat-fee-input-${kind}`}
        />
      </div>
      {fieldError && (
        <p
          className="text-xs text-red-600"
          data-testid={`processor-fee-error-${kind}`}
        >
          {fieldError}
        </p>
      )}
      {isManualP2P ? (
        <p className="text-xs text-muted-foreground italic">
          {PreflightCopy.ProcessorRateP2PHint}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {canonical.lookupUrl ? (
            <>
              {hint.split('[link]')[0]}
              <a
                href={canonical.lookupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-foreground"
              >
                {canonical.displayName} dashboard
              </a>
              {hint.split('[link]')[1] ?? ''}
            </>
          ) : (
            hint.replace('[link]', `${canonical.displayName} dashboard`)
          )}
        </p>
      )}
    </div>
  );
}

function DiscoveryPromptPreview() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PaymentProcessorKind>(PaymentProcessorKind.Numero);
  const { mutateAsync: confirmDiscovery, isPending } = useConfirmDiscovery();
  const [done, setDone] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-muted-foreground underline"
      >
        Preview discovery prompt
      </button>
    );
  }

  return (
    <div className="rounded-md border border-dashed border-border p-3 space-y-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        Discovery prompt preview
      </div>
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as PaymentProcessorKind)}
        className="rounded-md border border-border px-2 py-1 text-sm"
      >
        {Object.values(PaymentProcessorKind)
          .filter((k) => k !== PaymentProcessorKind.Other)
          .map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
      </select>
      <DiscoveryPrompt
        detectedKind={kind}
        onConfirm={async () => {
          await confirmDiscovery({ processorKind: kind, bankSourceProfileId: null, displayName: null });
          setDone(true);
        }}
        onSkip={() => setDone(true)}
        busy={isPending}
      />
      {done && (
        <div className="text-xs text-green-600">Done. The dialog returned.</div>
      )}
    </div>
  );
}
